import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import Button from './Button';
import Modal from './Modal';

const ConfirmationContext = createContext(null);
const PREFIX = 'bun-navigation:';

/** Use Provider's callback on the host Router so history owns PUSH/REPLACE/POP. */
function Provider({ children }) {
  const pending = useRef(null);
  const [dialog, setDialog] = useState(null);
  const settle = useCallback((allowed) => {
    const request = pending.current;
    if (!request) return;
    pending.current = null;
    setDialog(null);
    request.callback(allowed);
  }, []);
  const cancel = useCallback((id) => {
    if (pending.current?.id === id) settle(false);
  }, [settle]);
  const getUserConfirmation = useCallback((message, callback) => {
    // Legacy Prompt consumers retain the host router's normal confirmation.
    if (!message.startsWith(PREFIX)) {
      callback(window.confirm(message));
      return;
    }
    // One attempted transition owns the dialog. Reject repeated attempts.
    if (pending.current) {
      callback(false);
      return;
    }
    const [id, body] = JSON.parse(message.slice(PREFIX.length));
    pending.current = { id, callback };
    setDialog({ message: body });
  }, []);
  useEffect(() => () => {
    const request = pending.current;
    pending.current = null;
    request?.callback(false);
  }, []);
  const context = useMemo(() => ({ cancel }), [cancel]);
  return (
    <ConfirmationContext.Provider value={context}>
      {children(getUserConfirmation)}
      <Modal open={!!dialog} onClose={() => settle(false)} title="Leave without saving?"
        footer={<>
          <Button variant="secondary" onClick={() => settle(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => settle(true)}>Leave without saving</Button>
        </>}
      >
        {dialog?.message}
      </Modal>
    </ConfirmationContext.Provider>
  );
}

/** Dirty state and message belong to the screen; confirmation and unload protection live here. */
export default function NavigationPrompt({ when, message }) {
  const id = useId();
  const context = useContext(ConfirmationContext);
  useEffect(() => {
    if (!when) return undefined;
    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      context?.cancel(id);
    };
  }, [when, context, id]);
  // Standalone Router owners can supply their own confirmation callback.
  return <Prompt when={when} message={context ? PREFIX + JSON.stringify([id, message]) : message} />;
}

NavigationPrompt.Provider = Provider;
